import type { ContentHref, PersonHref, Proposal } from '@generalTypes/apiTypes';
import type { Person } from '@generalTypes/personApiTypes';
import type { RootState } from '@generalTypes/rootStateTypes';
import {
  selectApiWithPendingChanges,
  selectApiWithPendingChangesRelationsToAndFromMap,
  selectContentItem,
  selectPathToRoot,
  selectPendingActionsContentMap,
  selectProposalsForContentMap,
  selectBaselineRelationsToAndFromMap,
  selectBaselineContentItem,
  selectRawContentMap,
  selectSnapshotChangedHrefs,
} from '@newStore/documentApi/documentApiSelectors';
import { getPathToRoot } from '@newStore/externalData/externalDataHelpers';
import { pathMap } from '@newStore/externalData/externalDataTypes';
import {
  createTypedSelector,
  parentChildRelationFilter,
  stripHtml,
} from '@newStore/genericHelpers';
import { selectUserHref } from '@newStore/user/userSelectors';
import { isWebTabComponent } from '@nodeTypeConfig/configTypes';
import { shallowEqual } from 'react-redux';
import { getAllDescendantHrefs } from '../documentUIHelpers';
import { selectHiddenDescendants, selectTableOfContentChildren } from '../documentUISelectors';
import type { Mode } from '../documentUIState';
import type { DiffMessage, ProposalMetaInfo, ProposalType } from '../documentUITypes';
import { selectAppliedNodeConfig, selectDocumentRootType } from '../nodeTypeConfigSelectors';
import { getProposalType } from './diffTextCalculator';
import {
  getAllDescendantProposals,
  getProposalForNode,
  mergeProposals,
  transformProposal,
  wrapNodeTypeLabel,
} from './proposalHelpers';

const emptyArray = [];

/**
 * this selector gets all hrefs that are proposed to be deleted.
 * it is done this way because you can have a proposal to delete a relation to an IS_INCLUDED_IN node (attachment, teaser) that in itself won't be deleted.
 * yet we want to mark it as deleted for this case.
 */
export const selectProposedContentHrefsToDelete = createTypedSelector(
  [
    (state) => selectProposalsForContentMap(state),
    (state) => selectApiWithPendingChanges(state).relationsMap,
    (state) => selectApiWithPendingChanges(state).snapshotDeletedHrefs,
  ],
  (proposals, relationsMap, snapshotDeletedHrefs) => {
    const proposedContentHrefsToDelete: ContentHref[] = [];
    Object.values(proposals).forEach((proposal) => {
      proposal.listOfRequestedChanges.forEach((change) => {
        if (change.type === 'DELETE') {
          const relation = relationsMap[change.appliesTo.href];
          if (
            relation &&
            (relation.relationtype === 'IS_INCLUDED_IN' || relation.relationtype === 'IS_PART_OF')
          ) {
            proposedContentHrefsToDelete.push(relation.from.href);
          }
        }
      });
    });
    // items deleted since the selected snapshot are shown with the same 'deleted' style
    // as delete proposals (and are excluded from validation / drag and drop the same way)
    snapshotDeletedHrefs.forEach((href) => {
      proposedContentHrefsToDelete.push(href);
    });
    return proposedContentHrefsToDelete;
  },
  {
    memoizeOptions: {
      resultEqualityCheck: shallowEqual,
    },
  },
);

const getTransformedProposalWithChildProposalInfo = (
  proposal: Proposal | null,
  type: ProposalType,
  allDescendentProposals: Proposal[],
  personsMap: Record<string, Person>,
  userHref: PersonHref,
  mode: Mode,
) => {
  const descendentSummaryProposal = mergeProposals(allDescendentProposals);
  if (proposal) {
    // the node has a proposal itself AND there are children that have a proposal
    return transformProposal(
      {
        ...proposal,
        creators: [
          ...new Set([
            ...proposal.creators,
            ...(descendentSummaryProposal ? descendentSummaryProposal.creators : []),
          ]),
        ],
      },
      personsMap,
      userHref,
      allDescendentProposals.length,
      type,
      mode,
    );
  }
  if (descendentSummaryProposal) {
    // the node has no proposal for itself, but it has children with proposals
    return transformProposal(
      descendentSummaryProposal,
      personsMap,
      userHref,
      allDescendentProposals.length,
      type,
      mode,
    );
  }
  return null;
};

export const selectProposalMetaInfo = createTypedSelector(
  [
    (state, href: ContentHref) => selectContentItem(state, href),
    (state, href: ContentHref) => selectBaselineContentItem(state, href),
    (state) => selectProposalsForContentMap(state),
    (state) => selectApiWithPendingChangesRelationsToAndFromMap(state).to,
    (state, href: ContentHref) => selectHiddenDescendants(state, href),
    selectProposedContentHrefsToDelete,
    (state) => state.externalData.data[pathMap.persons].items,
    (state) => selectUserHref(state),
    (state, href: string) => state.documentUI.collapsedNodes[href],
    (state, href: ContentHref) => href,
    (state) => state.documentUI.mode,
    (state) => selectPendingActionsContentMap(state),
    (state) => selectSnapshotChangedHrefs(state),
  ],
  (
    contentItem,
    rawContentItem,
    proposalsMap,
    toRelationsMap,
    hiddenDescendants,
    proposedHrefsToDelete,
    personsMap,
    userHref,
    isCollapsed,
    href,
    mode,
    pendingActionsContentMap,
    snapshotChangedHrefs,
  ): ProposalMetaInfo | null => {
    if (!userHref || !mode) {
      return null;
    }

    if (mode === 'EDIT') {
      // return null;
      let proposalType: ProposalType = 'UPDATE';

      if (contentItem.$$meta.deleted) {
        proposalType = 'DELETE';
      } else if (!rawContentItem) {
        proposalType = 'CREATE';
      }

      let descendantsWithChangesCount = 0;
      let snapshotDescendantsCount = 0;

      if (isCollapsed) {
        const descendantHrefs = getAllDescendantHrefs(toRelationsMap, href);

        descendantsWithChangesCount = descendantHrefs.filter((z) =>
          pendingActionsContentMap.get(z),
        ).length;

        if (snapshotChangedHrefs.size > 0) {
          snapshotDescendantsCount = descendantHrefs.filter((z) =>
            snapshotChangedHrefs.has(z),
          ).length;
        }
      }

      const itemHasChanges = pendingActionsContentMap.get(href);
      const hasSnapshotDiff = snapshotChangedHrefs.has(href) || snapshotDescendantsCount > 0;

      if (!itemHasChanges && !descendantsWithChangesCount && !hasSnapshotDiff) {
        return null;
      }

      return transformProposal(
        null,
        personsMap,
        userHref,
        Math.max(descendantsWithChangesCount, snapshotDescendantsCount),
        proposalType,
        mode,
      );
    }

    const proposal = getProposalForNode(href, proposalsMap, contentItem, hiddenDescendants);
    if (isCollapsed) {
      // use case for collapsible nodes where there needs to be an indicator if it has children that have proposals
      // it is not a merge in the same way as with the hidden proposals.
      // We just say there are proposals for the children but we do not specify the changes
      const allDescendentProposals = getAllDescendantProposals(
        proposalsMap,
        toRelationsMap,
        contentItem.$$meta.permalink,
      );
      if (allDescendentProposals.length) {
        return getTransformedProposalWithChildProposalInfo(
          proposal,
          proposal
            ? getProposalType(contentItem, proposal, proposedHrefsToDelete)
            : 'CHILDREN_HAVE_PROPOSALS',
          allDescendentProposals,
          personsMap,
          userHref,
          mode,
        );
      }
    }
    if (!proposal) {
      return null;
    }
    const proposalType = getProposalType(contentItem, proposal, proposedHrefsToDelete);
    return transformProposal(proposal, personsMap, userHref, 0, proposalType, mode);
  },
);

export const selectProposalMetaInfoForToc = createTypedSelector(
  [
    (state, href: ContentHref) => selectContentItem(state, href),
    (state, href: ContentHref) => selectBaselineContentItem(state, href),
    (state) => selectProposalsForContentMap(state),
    (state) => selectApiWithPendingChangesRelationsToAndFromMap(state).to,
    (state, href: ContentHref) => selectHiddenDescendants(state, href),
    selectProposedContentHrefsToDelete,
    (state) => state.externalData.data[pathMap.persons].items,
    (state) => selectUserHref(state),
    (state, href: ContentHref) => selectTableOfContentChildren(state, href),
    (state, href: ContentHref) => href,
    (state) => state.documentUI.mode,
    (state) => selectPendingActionsContentMap(state),
    (state) => selectSnapshotChangedHrefs(state),
  ],
  (
    contentItem,
    rawContentItem,
    proposalsMap,
    toRelationsMap,
    hiddenDescendants,
    proposedHrefsToDelete,
    personsMap,
    userHref,
    tocChildHrefs,
    href,
    mode,
    pendingActionsContentMap,
    snapshotChangedHrefs,
  ): ProposalMetaInfo | null => {
    if (!userHref || !mode) {
      return null;
    }

    if (mode === 'EDIT') {
      // return null;
      let proposalType: ProposalType = 'UPDATE';

      if (contentItem.$$meta.deleted) {
        proposalType = 'DELETE';
      } else if (!rawContentItem) {
        proposalType = 'CREATE';
      }

      const descendantHrefs = getAllDescendantHrefs(toRelationsMap, href, new Set(tocChildHrefs));

      const descendantsWithChangesCount = descendantHrefs.filter((z) =>
        pendingActionsContentMap.get(z),
      ).length;

      const itemHasChanges = pendingActionsContentMap.get(href);

      // Show indicator if any hidden (non-TOC) descendant has a snapshot diff
      const snapshotDescendantsCount =
        snapshotChangedHrefs.size > 0
          ? descendantHrefs.filter((z) => snapshotChangedHrefs.has(z)).length
          : 0;
      const hasSnapshotDiff = snapshotChangedHrefs.has(href) || snapshotDescendantsCount > 0;

      if (!itemHasChanges && !descendantsWithChangesCount && !hasSnapshotDiff) {
        return null;
      }

      return transformProposal(
        null,
        personsMap,
        userHref,
        Math.max(descendantsWithChangesCount, snapshotDescendantsCount),
        proposalType,
        mode,
      );
    }

    const proposal = getProposalForNode(href, proposalsMap, contentItem, hiddenDescendants);
    const allDescendentProposals = getAllDescendantProposals(
      proposalsMap,
      toRelationsMap,
      href,
      tocChildHrefs,
    );
    if (allDescendentProposals.length) {
      // toc row needs to be an indicator if it has children that are not in the toc and have proposals
      // it is not a merge in the same way as with the hidden proposals.
      // We just say there are proposals for the children but we do not specify the changes
      return getTransformedProposalWithChildProposalInfo(
        proposal,
        proposal
          ? getProposalType(contentItem, proposal, proposedHrefsToDelete)
          : 'CHILDREN_HAVE_PROPOSALS',
        allDescendentProposals,
        personsMap,
        userHref,
        mode,
      );
    }
    if (!proposal) {
      return null;
    }
    const proposalType = getProposalType(contentItem, proposal, proposedHrefsToDelete);
    return transformProposal(proposal, personsMap, userHref, 0, proposalType, mode);
  },
);

export const selectProposalMetaInfoAside = createTypedSelector(
  [
    (state, href: ContentHref) => selectContentItem(state, href),
    (state, href: ContentHref) => selectBaselineContentItem(state, href),
    (state) => selectProposalsForContentMap(state),
    (state, href: ContentHref) => selectHiddenDescendants(state, href),
    (state) => state.externalData.data[pathMap.persons].items,
    (state) => selectUserHref(state),
    (state, href: ContentHref) => href,
    (state) => state.documentUI.mode,
  ],
  (
    contentItem,
    rawContentItem,
    proposalsMap,
    hiddenDescendants,
    personsMap,
    userHref,
    href,
    mode,
  ): ProposalMetaInfo | null => {
    if (!userHref || !mode) {
      return null;
    }

    let proposalType: ProposalType = 'UPDATE';

    if (contentItem.$$meta.deleted) {
      proposalType = 'DELETE';
    } else if (!rawContentItem) {
      proposalType = 'CREATE';
    }

    if (mode === 'EDIT') {
      return transformProposal(null, personsMap, userHref, 0, proposalType, mode);
    }

    const proposal = getProposalForNode(href, proposalsMap, contentItem, hiddenDescendants);

    if (!proposal) {
      return null;
    }

    return transformProposal(proposal, personsMap, userHref, 0, proposalType, mode);
  },
);

export const selectNodeProposal = createTypedSelector(
  [
    (state, href: ContentHref) => selectContentItem(state, href),
    (state) => selectProposalsForContentMap(state),
    (state, href: ContentHref) => selectHiddenDescendants(state, href),
    (state, href: ContentHref) => href,
  ],
  (content, proposalsMap, hiddenDescendants, href): Proposal | null => {
    return getProposalForNode(href, proposalsMap, content, hiddenDescendants);
  },
);

export const selectNodeDiffText = (
  state: RootState,
  href: ContentHref,
  parentHref: ContentHref | null,
) => {
  const documentType = selectDocumentRootType(state);

  if (!documentType) {
    return emptyArray;
  }

  const nodeTypeConfig = selectAppliedNodeConfig(state, href);
  const rawContentMap = selectRawContentMap(state);
  const contentItem = selectContentItem(state, href);
  const rawFromRelations =
    selectBaselineRelationsToAndFromMap(state).from[href]?.filter(parentChildRelationFilter) || [];
  const rawRelationToParent = rawFromRelations.find((z) => z.to.href === parentHref);
  const fromRelations =
    selectApiWithPendingChangesRelationsToAndFromMap(state).from[href]?.filter(
      parentChildRelationFilter,
    ) || [];
  const relationToParent = fromRelations.find((z) => z.to.href === parentHref);
  const rawContentItem = selectBaselineContentItem(state, href);
  const nodeTypeLabel = nodeTypeConfig.information.single.split(':').pop();
  const { edit, audienceTab, webTab } = nodeTypeConfig;

  const diffs: DiffMessage[] = [];

  /**
   * there are 3 changemessages not related to the aside
   * 1) node created
   * 2) node deleted
   * 3) node moved/relocated
   */

  if (contentItem.$$meta.deleted) {
    const deletedValue = rawContentItem?.title;
    diffs.push({
      message: deletedValue
        ? wrapNodeTypeLabel`${nodeTypeLabel} "${deletedValue}" verwijderd`
        : wrapNodeTypeLabel`${nodeTypeLabel} verwijderd`,
    });
  } else if (
    !rawContentItem ||
    (!rawRelationToParent && relationToParent?.relationtype === 'IS_INCLUDED_IN') // for IS_INCLUDED_IN relations, the rawContentItem is present and unchanged.
  ) {
    diffs.push({ message: wrapNodeTypeLabel`${nodeTypeLabel} toegevoegd` });
  }

  // find the raw relation by key because the parent could have changed.
  const rawRelation = rawFromRelations.find((z) => z.key === relationToParent?.key);

  // check if node has moved.
  if (
    relationToParent &&
    rawRelation &&
    (rawRelation.readorder !== relationToParent.readorder ||
      rawRelation.to.href !== relationToParent.to.href)
  ) {
    const currentPath = selectPathToRoot(state, href);
    const rawPath = getPathToRoot(selectBaselineRelationsToAndFromMap(state), rawContentMap, href);

    const newValue = currentPath
      .map((c) => stripHtml(c?.title))
      .slice(0, -1)
      .toReversed()
      .slice(0, -1)
      .join(' > ');
    const oldValue = rawPath
      .map((c) => stripHtml(c?.title))
      .slice(0, -1)
      .toReversed()
      .slice(0, -1)
      .join(' > ');

    let diffHtml = '';
    if (newValue !== oldValue) {
      diffHtml = `van pad: ${oldValue}`;
    }

    diffs.push({
      message: wrapNodeTypeLabel`${nodeTypeLabel} verplaatst ${diffHtml}.`,
    });
  }

  [...edit, ...(audienceTab || []), ...(webTab || [])].forEach((config) => {
    if ('asideChangeMessageSelector' in config) {
      const message = config.asideChangeMessageSelector(
        state,
        href,
        // @ts-expect-error config is not typed correctly
        config,
      );

      if (message !== null) {
        const label = isWebTabComponent(config)
          ? `Web ${config.label.toLowerCase()}`
          : config.label;
        diffs.push({
          property: config.property,
          message: `<span class="property-label">${label}</span>: ${message}`,
        });
      }
    }
  });

  return diffs.map((dm) => `<li>${dm.message}</li>`).join('');
};
